/**
 * Live re-reads for Partner Detail's relationship ledger (#3820) — a THIN
 * SELECTOR over the shared realtime bus (`src/realtime/`).
 *
 * The page holds its OWN copy of the partner's ledger, so a `relationship-items`
 * event the ServiceStore merged never reached the screen: a colleague's new
 * expectation, a status change or a removal showed only after a reload, though
 * the API publishes all of them to staff (STAFF_BROADCAST_CRUD_PATHS; a row from
 * a private meeting only to the people who may see it). The answer is the one
 * the page's own writes already give — re-read — because the server applies the
 * ledger's scope, order, limit and visibility, and the read drops a row removed
 * while this tab was not listening (`findAndEvict` in the store).
 *
 * Two kinds of event move it:
 *  - `relationship-items` CRUD (the Clients area's `clientCrudChannel` — the one
 *    translator for those wire events) for a row whose subject is this partner,
 *    or a row the page lists (a payload that does not carry the subject);
 *  - the pipeline saying its relationship signals are written — Gantry's push
 *    (`meeting-analyzed`, the default) or the end of HQ's own pass's enrichment
 *    (`meeting-enriched`; its `meeting-processed` fires BEFORE the signals
 *    exist) — since it writes them straight to the table, so those rows emit
 *    no CRUD event.
 *
 * A burst is one re-read (`coalesce`).
 */
import type RootStore from "stores/RootStore";
import { clientCrudChannel, subscribeRealtime } from "realtime";
import { coalesce } from "shared/coalesce";
import { subscribeMeetingIntelEvents } from "../meetingIntelligenceRealtime";

/** How long a burst of ledger events is gathered into one re-read. */
export const PARTNER_LEDGER_LIVE_DEBOUNCE_MS = 250;

/** What the page has open, asked afresh on every event. */
export interface WatchedLedger {
  partnerId: string | null;
  /** The ids of the rows the page lists, as strings. */
  listedIds: ReadonlySet<string>;
}

/** Is this ledger row (as an event carries it) on the open partner's ledger? */
export function isOnPartnerLedger(record: Record<string, unknown>, watched: WatchedLedger): boolean {
  if (!watched.partnerId) return false;
  if (record.subjectType === "partner" && String(record.subjectId ?? "") === watched.partnerId) return true;
  // A payload without the subject (a write made with `$select`) can still be a listed row.
  return record.id !== undefined && record.id !== null && watched.listedIds.has(String(record.id));
}

/**
 * Call `reread` once per burst of events that change the open partner's
 * ledger. Returns an idempotent disposer, which also drops a waiting re-read.
 */
export function watchPartnerLedger(
  rootStore: RootStore,
  watched: () => WatchedLedger,
  reread: () => void,
  debounceMs: number = PARTNER_LEDGER_LIVE_DEBOUNCE_MS
): () => void {
  const soon = coalesce(reread, debounceMs);
  const offRows = subscribeRealtime(rootStore, clientCrudChannel, (event) => {
    if (event.service !== "relationship-items") return;
    if (isOnPartnerLedger(event.record, watched())) soon();
  });
  // shortcut: the pipeline's events name the meeting's CLIENT, never its
  // partners, so every analysis that wrote signals re-reads an open Partner
  // page's ledger (one find of at most 200 rows). Exact, and cheap at the rate
  // meetings are analysed; carry the partner ids on the event if that grows.
  const offPipeline = subscribeMeetingIntelEvents(rootStore, (event) => {
    if (!watched().partnerId) return;
    if (event.kind === "meeting-analyzed") {
      // Gantry's push says how many signals it wrote; none means no ledger row moved.
      if (Number(event.payload.signalsWritten) === 0) return;
      soon();
    } else if (event.kind === "meeting-enriched") {
      // HQ's own pass: its signals are written once this fires, never at
      // `meeting-processed`, which is why that one re-reads nothing (Codex r1).
      soon();
    }
  });
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    soon.cancel();
    offRows();
    offPipeline();
  };
}
